import React, { memo, MouseEventHandler, useCallback, useState } from 'react'
import AutocompleteTransfixUser, {
  TBaseValue,
} from 'transfix-ui/components/AutocompleteTransfixUser'
import Avatar from 'transfix-ui/components/Avatar'
import { Button } from 'transfix-ui/components/Button'
import Card from 'transfix-ui/components/Card'
import { InlineEditContainer } from 'transfix-ui/components/InlineEditContainer'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box, Typography } from 'transfix-ui/core'
import { IError } from 'types/graphqlTypes'
import { IAwardedLaneFragment } from 'apps/marketplace/pages/awarded-lanes/graphql/AwardedLaneFragment'
import { INameSearchResult } from 'apps/marketplace/utils/api/search'
import { EditIcon } from 'transfix-icons'
import { useCarrierSalesRep } from './hooks/useCarrierSalesRep'

export interface ICarrierSalesRepSelectorProps {
  awardedLaneId?: string
  carrierSalesRep: IAwardedLaneFragment['carrierSalesRep']
  loading?: boolean
  onChange?: (user?: Partial<INameSearchResult>) => void
}

const CarrierSalesRepSelector = memo<ICarrierSalesRepSelectorProps>(
  ({ awardedLaneId, carrierSalesRep, loading = false, onChange }) => {
    const { fullName: carrierSalesRepName, id: carrierSalesRepId } = carrierSalesRep || {}
    const salesRepDisplayName = carrierSalesRepName ?? 'Unassigned'

    const [user, setUser] = useState<TBaseValue | null>(
      carrierSalesRep
        ? {
            id: `${carrierSalesRepId}`,
            text: carrierSalesRepName ?? '',
          }
        : null
    )
    const { id: userId = null, text: userName } = user || {}

    const [errors, setErrors] = useState<IError[]>([])

    const [showInlineEdit, setShowInlineEdit] = useState<boolean>(false)
    const toggleInlineEdit = useCallback(
      () => setShowInlineEdit(showInlineEdit => !showInlineEdit),
      []
    )

    const { loading: carrierSalesRepLoading, reassign } = useCarrierSalesRep({
      awardedLaneId,
    })

    const handleSaveClick: MouseEventHandler = useCallback(async () => {
      if (userId !== `${carrierSalesRepId}`) {
        reassign(userId).then(({ errors }) => {
          if (errors && errors.length > 0) {
            setErrors(errors)
          } else {
            if (onChange) {
              onChange(
                userId
                  ? {
                      id: userId,
                      name: userName,
                    }
                  : undefined
              )
            }
            toggleInlineEdit()
          }
        })
      } else {
        toggleInlineEdit()
      }
    }, [userId, carrierSalesRepId, toggleInlineEdit, reassign, onChange, userName])

    return (
      <InlineEditContainer
        name='reassign-carrier-sales'
        dataTest='carrier-sales-inline-edit'
        editModeEnabled={showInlineEdit}
        onEditModeRequested={toggleInlineEdit}
        resetOnClickOutside={false}
        editMode={
          <Card>
            <Box>Carrier Sales</Box>
            {errors.map(({ key, message }) => (
              <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} key={key} message={message} />
            ))}
            <AutocompleteTransfixUser
              value={user}
              onChange={value => setUser(value)}
              renderInputType='withAvatar'
            />
            <Box textAlign='right' mt={2}>
              <Button color='tertiary' onClick={toggleInlineEdit} disabled={loading}>
                Cancel
              </Button>
              <Button
                color='primary'
                onClick={handleSaveClick}
                disabled={carrierSalesRepLoading || loading}
                data-test='save-carrier-sales-rep'>
                Save
              </Button>
            </Box>
          </Card>
        }>
        <Box mb={2}>
          <Typography variant='subtitle1' className='text-secondary'>
            Carrier Sales
          </Typography>
        </Box>
        <Box display='flex' alignItems='center' className='toggle-on-hover'>
          <Avatar name={salesRepDisplayName} size='small' hideTooltip />
          <Box display='flex' alignItems='center' ml={2}>
            <Box mr={1}>{salesRepDisplayName}</Box>
            <EditIcon size='large' className='showable success' color='skyBlue6' />
          </Box>
        </Box>
      </InlineEditContainer>
    )
  }
)

export default CarrierSalesRepSelector
